import React, { useEffect, useState, useCallback } from 'react';
import useAuthStore from '../../store/authStore';
import useProjectStore from '../../store/projectStore';
import useUIStore from '../../store/uiStore';
import * as expenseService from '../../services/expenseService';
import ExpenseTabs from '../../components/ExpenseTabs';
import ScrollX from '../../components/ScrollX';
import { BarChart } from '../../components/ExpenseCharts';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// One lumpsum figure per project per month for factory PO-level procurement
// (2026-09-08 user request) - deliberately not line-item like Vendor Bills;
// the user has ~200 components per project and explicitly chose one
// punched-in monthly number over per-invoice detail. See
// expense_routes.py's "MATERIAL PURCHASES" section.
//
// Also the home for the "Total project cost" view: Site Expenses + Vendor
// Bills (site-side vendor spend, unchanged) + this table (factory-side
// bulk procurement) - three independent buckets, no overlap, per the
// user's own framing.
const blankForm = { sales_order_number: '', period: '', amount: '', notes: '' };

const monthLabel = (isoDate) => {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const MaterialPurchasesPage = () => {
  const { user } = useAuthStore();
  const { projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [editingId, setEditingId] = useState(null);
  // All-projects view, shown when nothing is selected (2026-09-11 user
  // request: "show me top 10 costs by projects, even if none of the
  // projects is selected"). Loaded once on mount, independent of the
  // per-project state above - one extra request, aggregated client-side
  // since /materials/purchases with no project_id already returns every
  // row (no new backend endpoint needed).
  const [allPurchases, setAllPurchases] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const canLog = !!user; // everyone can log money - 2026-08-19 convention, see expense_routes.py

  // 'all' statuses, not just active (2026-09-11 bug fix) - a completed
  // project still needs its material purchases visible/nameable (the
  // user's own stated use case is totaling a project's cost *after* it
  // wraps, not just while it's running); the default active-only fetch
  // was both hiding completed projects from the picker and showing "Project
  // #N" instead of a name for any purchase linked to one.
  useEffect(() => {
    fetchProjects('all');
  }, [fetchProjects]);

  useEffect(() => {
    setIsLoadingAll(true);
    expenseService.getMaterialPurchases({})
      .then((res) => setAllPurchases(res.data.purchases || []))
      .catch(() => showToast('Failed to load top projects', 'error'))
      .finally(() => setIsLoadingAll(false));
  }, [showToast]);

  const topProjectsData = React.useMemo(() => {
    const totals = {};
    for (const p of allPurchases) {
      totals[p.project_id] = (totals[p.project_id] || 0) + Number(p.amount || 0);
    }
    return Object.entries(totals)
      .map(([projectId, amount]) => ({
        project_id: Number(projectId),
        project: projects.find((p) => p.id === Number(projectId))?.name || `Project #${projectId}`,
        amount,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [allPurchases, projects]);

  const loadForProject = useCallback(async (projectId) => {
    if (!projectId) {
      setPurchases([]);
      setSummary(null);
      return;
    }
    setIsLoading(true);
    try {
      const [purchasesRes, summaryRes] = await Promise.all([
        expenseService.getMaterialPurchases({ project_id: projectId }),
        expenseService.getProjectCostSummary(projectId),
      ]);
      setPurchases(purchasesRes.data.purchases || []);
      setSummary(summaryRes.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load material purchases', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadForProject(selectedProjectId);
  }, [selectedProjectId, loadForProject]);

  const lifetimeTotal = purchases.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  // Chronological (purchases itself is sorted newest-first, for the table)
  // and re-labeled for the chart's x-axis - see the "monthly purchase
  // trend" chart option (2026-09-08).
  const trendData = [...purchases].reverse().map((p) => ({ month: monthLabel(p.period), amount: p.amount }));

  const resetForm = () => {
    setForm(blankForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (purchase) => {
    setForm({
      sales_order_number: purchase.sales_order_number || '',
      period: purchase.period.slice(0, 7), // YYYY-MM for the month input
      amount: purchase.amount,
      notes: purchase.notes || '',
    });
    setEditingId(purchase.id);
    setShowForm(true);
  };

  const handleDelete = async (purchase) => {
    if (!window.confirm(`Delete the ${monthLabel(purchase.period)} entry? This cannot be undone.`)) return;
    try {
      await expenseService.deleteMaterialPurchase(purchase.id);
      showToast('Entry deleted', 'success');
      loadForProject(selectedProjectId);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete entry', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProjectId) {
      showToast('Select a project first', 'error');
      return;
    }
    if (!form.period || !form.amount) {
      showToast('Month and amount are required', 'error');
      return;
    }
    try {
      if (editingId) {
        await expenseService.updateMaterialPurchase(editingId, {
          sales_order_number: form.sales_order_number || null,
          period: `${form.period}-01`,
          amount: Number(form.amount),
          notes: form.notes || null,
        });
        showToast('Entry updated', 'success');
      } else {
        await expenseService.createMaterialPurchase({
          project_id: Number(selectedProjectId),
          sales_order_number: form.sales_order_number || null,
          period: `${form.period}-01`,
          amount: Number(form.amount),
          notes: form.notes || null,
        });
        showToast('Purchase logged', 'success');
      }
      resetForm();
      loadForProject(selectedProjectId);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to save entry', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <ExpenseTabs />
        <h1 className="text-2xl font-bold text-white mb-6">Material Purchases</h1>

        <div className="glass-card p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={selectedProjectId}
            onChange={(e) => { setSelectedProjectId(e.target.value); resetForm(); }}
            className="w-full md:w-96 border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">Select a project</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {selectedProjectId && summary && (
          <div className="glass-card p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Total project cost — {summary.project_name}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Site expenses</p>
                <p className="text-xl font-semibold text-gray-900">{money(summary.site_expenses_total)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Vendors (at site)</p>
                <p className="text-xl font-semibold text-gray-900">{money(summary.vendor_bills_total)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Material purchase (factory)</p>
                <p className="text-xl font-semibold text-gray-900">{money(summary.material_purchase_total)}</p>
              </div>
              {/* Labour added 2026-09-29 (production-tracker daily reports'
                  attendance-adjusted salary cost) per user request - no
                  budget field yet, spend only, same as the dashboard's
                  Actual vs budget panel. */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Labour (production tracker)</p>
                <p className="text-xl font-semibold text-gray-900">{money(summary.labour_cost_total)}</p>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-4 flex justify-between items-baseline">
              <span className="text-sm text-gray-500">Total project cost</span>
              <span className="text-2xl font-bold text-gray-900">{money(summary.total_project_cost)}</span>
            </div>
          </div>
        )}

        {selectedProjectId && canLog && (
          <div className="glass-card p-6 mb-6">
            <button onClick={() => (showForm ? resetForm() : setShowForm(true))} className="text-sm font-semibold text-komal-blue hover:underline">
              {showForm ? 'Cancel' : '+ Log a material purchase'}
            </button>
            {showForm && (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales order number</label>
                  <input
                    value={form.sales_order_number}
                    onChange={(e) => setForm({ ...form, sales_order_number: e.target.value })}
                    placeholder="SO-2026-0211"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                  <input
                    type="month"
                    required
                    value={form.period}
                    onChange={(e) => setForm({ ...form, period: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input
                    type="number" min="0" step="0.01" required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Bulk component order - batch 3"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" className="bg-komal-blue text-white text-sm font-semibold px-4 py-2 rounded hover:opacity-90">
                    {editingId ? 'Save changes' : 'Save'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {selectedProjectId && purchases.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <BarChart title="Material purchase by month" data={trendData} labelKey="month" valueKey="amount" />
          </div>
        )}

        {selectedProjectId && (
          <div className="glass-card mb-6">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Purchases by month ({purchases.length})</h2>
            </div>
            {isLoading ? (
              <p className="text-gray-500 text-sm p-5">Loading...</p>
            ) : purchases.length === 0 ? (
              <p className="text-gray-500 text-sm p-5">No material purchases logged yet for this project.</p>
            ) : (
              <ScrollX>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-100">
                      <th className="px-4 py-2 font-medium">Month</th>
                      <th className="px-4 py-2 font-medium">SO number</th>
                      <th className="px-4 py-2 font-medium">Amount</th>
                      <th className="px-4 py-2 font-medium">Notes</th>
                      <th className="px-4 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p) => (
                      <tr key={p.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-3">{monthLabel(p.period)}</td>
                        <td className="px-4 py-3 text-gray-500">{p.sales_order_number || '—'}</td>
                        <td className="px-4 py-3 font-medium">{money(p.amount)}</td>
                        <td className="px-4 py-3 text-gray-500">{p.notes || '—'}</td>
                        <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                          {canLog && (
                            <>
                              <button onClick={() => handleEdit(p)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                              <button onClick={() => handleDelete(p)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200">
                      <td className="px-4 py-3 font-semibold text-gray-900" colSpan={2}>Total (project lifetime)</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{money(lifetimeTotal)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </ScrollX>
            )}
          </div>
        )}

        {!selectedProjectId && (
          <div className="glass-card p-6">
            {isLoadingAll ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : (
              <BarChart
                title="Top 10 projects by material purchase (lifetime)"
                data={topProjectsData}
                labelKey="project"
                valueKey="amount"
                horizontal
                onBarClick={(row) => setSelectedProjectId(String(row.project_id))}
              />
            )}
            <p className="text-xs text-gray-400 mt-4">Select a project above (or click a bar) to log or view its material purchases in detail.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialPurchasesPage;

